import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { UserGateway } from 'src/app/application/gateway/UserGateway';
import { Country } from 'src/app/domain/models/Country';
import { Profile } from 'src/app/domain/models/Profile';
import { createPasswordStrengthValidator } from 'src/app/ui/util/custom-validators';
import { ProfileUtils } from 'src/app/ui/util/profile-utils';
import {formLimit} from 'src/app/ui/util/form-limit';

@Component({
  selector: 'app-admin-edit-user',
  templateUrl: './admin-edit-user.component.html',
  styleUrls: ['./admin-edit-user.component.css'],

})
export class AdminEditUserComponent implements OnInit {

  @Input() profileId: number | undefined
  @Output() closeDialog = new EventEmitter();

  profileTypeOptions: any[] = ProfileUtils.getProfileTypeOptions()
  profile: Profile | undefined
  countries: Country[] = []

  loading: boolean = false
  submit: boolean = false
  formLimit = formLimit;

  formGroup: FormGroup = this._formBuilder.group({
    profileType: [{value: '', disabled: true}, Validators.required],
    contactName: ['', [Validators.required, Validators.maxLength(this.formLimit.nameLimit)]],
    contactLastName: [null, [Validators.required, Validators.maxLength(this.formLimit.nameLimit)]],
    contactCountryId: [null, Validators.required],
    contactCity: [''],
    contactAddress: ['', Validators.maxLength(this.formLimit.addressLimit)],
    contactPhone: [''],
    contactEmail: [{value: ''}, [Validators.required, Validators.email, Validators.maxLength(this.formLimit.mailLimit)]],
    vat: ['', Validators.maxLength(this.formLimit.vatLimit)],
    companyName: ['', Validators.maxLength(this.formLimit.companyName)],
    sagCode: [null],
    legalRepresentativeName: [''],
    legalRepresentativeNumber: [null, Validators.maxLength(this.formLimit.legalRepresentativeNumber)],
    hasTwoAuthetication: [false],
    isVerified: [false],
    password: [null, [createPasswordStrengthValidator(), Validators.minLength(8)]]
  })

  constructor(
    private _messageService: MessageService,
    private _formBuilder: FormBuilder,
    private _profileService: ProfileGateway,
    private _countryService: CountryGateway,
    private _authService: AuthGateway,
    private _userService: UserGateway,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.profileId) {
      this.loadCountries()
      this.loadProfile()
    }
  }

  loadForm() {
    if(this.profile) {
      this.formGroup.controls["profileType"].setValue(this.profile.type)
      this.formGroup.controls["contactName"].setValue(this.profile.contactName)
      this.formGroup.controls["contactLastName"].setValue(this.profile.contactLastName)
      this.formGroup.controls["contactEmail"].setValue(this.profile.contactEmail)
      this.formGroup.controls["contactCountryId"].setValue(this.profile.contactCountryId)
      this.formGroup.controls["contactCity"].setValue(this.profile.contactCity)
      this.formGroup.controls["contactAddress"].setValue(this.profile.contactAddress)
      this.formGroup.controls["contactPhone"].setValue(this.profile.contactPhone)
      this.formGroup.controls["vat"].setValue(this.profile.vat)
      this.formGroup.controls["companyName"].setValue(this.profile.companyName)
      this.formGroup.controls["sagCode"].setValue(this.profile.sagCode)
      this.formGroup.controls["legalRepresentativeName"].setValue(this.profile.legalRepresentativeName)
      this.formGroup.controls["legalRepresentativeNumber"].setValue(this.profile.legalRepresentativeNumber)
      this.formGroup.controls["hasTwoAuthetication"].setValue(this.profile.hasTwoAuthetication)
      this.formGroup.controls["isVerified"].setValue(this.profile.isVerified)
    }
  }

  loadProfile(): void {
    if(this.profileId) {
      this._profileService.getById(this.profileId).subscribe({
        next: (v) => {
          this.profile = v
          this.loadForm()
        },
        error: (e) => {
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudo recuperar el perfil.' });
        }
      })
    }
  }

  loadCountries(): void {
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
      },
      error: (e) => {
        this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudo recuperar la lista de paises.' });
      }
    })
  }

  save(): void {
    this.submit = true
    if(this.profile && this.formGroup.valid) {
      this.loading = true

      this.profile.type = this.formGroup.controls["profileType"].value
      this.profile.contactName = this.formGroup.controls["contactName"].value
      this.profile.contactLastName = this.formGroup.controls["contactLastName"].value
      this.profile.contactEmail = this.formGroup.controls["contactEmail"].value
      this.profile.contactCountryId = this.formGroup.controls["contactCountryId"].value
      this.profile.contactCity = this.formGroup.controls["contactCity"].value
      this.profile.contactAddress = this.formGroup.controls["contactAddress"].value
      this.profile.contactPhone = this.formGroup.controls["contactPhone"].value
      this.profile.vat = this.formGroup.controls["vat"].value
      this.profile.companyName = this.formGroup.controls["companyName"].value
      this.profile.sagCode = this.formGroup.controls["sagCode"].value ?? undefined
      this.profile.legalRepresentativeName = this.formGroup.controls["legalRepresentativeName"].value
      this.profile.legalRepresentativeNumber = this.formGroup.controls["legalRepresentativeNumber"].value
      this.profile.hasTwoAuthetication = this.formGroup.controls["hasTwoAuthetication"].value
      this.profile.isVerified = this.formGroup.controls["isVerified"].value
      this.profile.updaterUsername = this._authService.getUsername()

      // Solo incluir password si tiene un valor
      const passwordValue = this.formGroup.controls["password"].value
      if (passwordValue) {
        this.profile.password = passwordValue
      }

      this._profileService.updateAdmin(this.profile).subscribe({
        next: (v) => {
          this.profile = v
          this.loadForm()
          this.submit = false
          this.loading = false
          this.cancel(true)
        },
        error: (e) => {
          this.submit = false
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudo actualizar el usuario. Inténtelo de nuevo.' });
        }
      })
    }
    else {
      this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'Hay errores en el formulario. Por favor, validar.' });
      this.loading = false
    }
  }

  resetPassword() {
    if(this.profile) {
      let request = {
        username: this.profile.contactEmail
      }
      this._userService.resetPasswordRequestToken(request).subscribe({
        next: (v) => {
          this._messageService.add({ severity: 'success', summary: 'Acción exitosa', detail: 'Se ha solicitado el restablecimiento de contraseña correctamente.' })
          this.submit = false
          this.loading = false
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudo solicitar el restablecimiento de contraseña.' });
        }
      })
    }
  }

  cancel(isReload?: boolean): void {
    this.closeDialog.emit(isReload)
  }

  getErrorMessage(fieldName: string): string {
    if (this.formGroup.controls[fieldName].hasError('required')) {
      return 'Este campo es requerido.';
    }
    if (this.formGroup.controls[fieldName].hasError('email')) {
      return 'Formato de correo electrónico incorrecto.';
    }
    if (this.formGroup.controls[fieldName].hasError('maxlength')) {
      const maxCharacters = this.formGroup.controls[fieldName].errors?.['maxlength']?.requiredLength;
      return `Máximo ${maxCharacters} caracteres permitidos.`;
    }
    if (this.formGroup.controls[fieldName].hasError('minlength')) {
      const minCharacters = this.formGroup.controls[fieldName].errors?.['minlength']?.requiredLength;
      return `Mínimo ${minCharacters} caracteres requeridos.`;
    }
    if (this.formGroup.controls[fieldName].hasError('passwordStrength')) {
      return this.translate.instant('FormErrorMessage.StrongPasswordError')
    }
    return '';
  }

}
